import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  EmojiEvents as ChallengesIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ChallengeCard from './ChallengeCard';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
  transition: 'box-shadow 0.3s ease-in-out',
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
}));

const ChallengesHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontSize: '1.5rem',
}));

const JoinCreateButton = styled(Button)(({ theme }) => ({
  borderRadius: 24,
  textTransform: 'none',
  fontWeight: 'bold',
  padding: theme.spacing(1, 3),
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.3s ease-in-out',
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(6, 2),
  color: theme.palette.text.secondary,
}));

const LoadingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

function ChallengesList({ 
  challenges = [],
  loading = false,
  onChallengeClick,
  onJoinCreateClick
}) {
  if (loading) {
    return (
      <StyledCard>
        <CardContent>
          <HeaderBox>
            <ChallengesHeader>
              <ChallengesIcon color="primary" />
              CHALLENGES
            </ChallengesHeader>
            <JoinCreateButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onJoinCreateClick}
              disabled
            >
              Join / Create
            </JoinCreateButton>
          </HeaderBox>
          <LoadingBox>
            <CircularProgress />
          </LoadingBox>
        </CardContent>
      </StyledCard>
    );
  }

  if (!challenges || challenges.length === 0) {
    return (
      <StyledCard>
        <CardContent>
          <HeaderBox>
            <ChallengesHeader>
              <ChallengesIcon color="primary" />
              CHALLENGES
            </ChallengesHeader>
            <JoinCreateButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onJoinCreateClick}
            >
              Join / Create
            </JoinCreateButton>
          </HeaderBox>
          
          <EmptyStateBox>
            <ChallengesIcon 
              sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} 
            />
            <Typography variant="h6" gutterBottom>
              No Active Challenges
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Join an existing challenge or create your own to start competing with friends!
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onJoinCreateClick}
              size="large"
            >
              Get Started
            </Button>
          </EmptyStateBox>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <CardContent>
        <HeaderBox>
          <ChallengesHeader>
            <ChallengesIcon color="primary" />
            CHALLENGES
          </ChallengesHeader>
          <JoinCreateButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onJoinCreateClick}
          >
            Join / Create
          </JoinCreateButton>
        </HeaderBox>

        <Grid container spacing={2}>
          {challenges.map((challenge, index) => (
            <Grid item xs={12} sm={6} md={4} key={challenge.id || challenge.challenge_id}>
              <ChallengeCard
                challenge={challenge}
                onClick={onChallengeClick}
                backgroundIndex={index}
              />
            </Grid>
          ))}
        </Grid>

        {challenges.length > 6 && (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={onJoinCreateClick}
            >
              View All Challenges
            </Button>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
}

export default ChallengesList;